import { useQueryClient } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import {
  ListSharesOutput,
  DeleteShareInput,
  FolderTreeOutput,
} from "@zapier/bff";
import { trpc } from "../trpc";

export function removeShareFromSearchQueryResults(
  cache: ListSharesOutput,
  share: DeleteShareInput,
): ListSharesOutput {
  return cache?.filter(
    (cachedShare) =>
      cachedShare.assetId !== share.assetId ||
      cachedShare.assetType !== share.assetType ||
      cachedShare.subjectId !== share.subjectId ||
      cachedShare.subjectType !== share.subjectType ||
      (cachedShare.group ?? undefined) !== (share.group ?? undefined),
  );
}

function markFolderAsUnshared(
  cache: FolderTreeOutput | undefined,
  folderId: string,
): FolderTreeOutput | undefined {
  if (!cache) {
    return undefined;
  }

  const index = cache.tree.findIndex((item) => item.id === folderId);

  // Skip updating if there's no matching folder or if it's already marked as unshared
  if (index === -1 || !cache.tree[index].isShared) {
    return cache;
  }

  // Mark the folder as unshared
  const nextTree = [...cache.tree];
  nextTree[index] = { ...nextTree[index], isShared: false };

  return { ...cache, tree: nextTree };
}

export function useDeleteAssetShare() {
  const queryClient = useQueryClient();

  const folderTreeQueryKey = getQueryKey(trpc.folder.folderTree, {}, "query");

  return trpc.permission.deleteShare.useMutation({
    onMutate: (share) => {
      const listSharesQueryKey = getQueryKey(
        trpc.permission.listShares,
        {
          assetId: share.assetId,
          assetType: share.assetType,
        },
        "query",
      );

      // Get the list of shares from the cache
      const previousShares =
        queryClient.getQueryData<ListSharesOutput>(listSharesQueryKey);

      if (!previousShares) {
        return;
      }

      const nextShares = removeShareFromSearchQueryResults(
        previousShares,
        share,
      );

      if (nextShares.length === previousShares.length) {
        return;
      }

      // Optimistically update the cache to remove the share
      queryClient.setQueryData(listSharesQueryKey, () => nextShares);

      let previousFolderTree: FolderTreeOutput | undefined;

      // Show as unshared if it's a folder and has no other shares after deletion
      if (share.assetType === "folder" && nextShares.length === 0) {
        previousFolderTree =
          queryClient.getQueryData<FolderTreeOutput>(folderTreeQueryKey);

        queryClient.setQueryData<FolderTreeOutput>(
          folderTreeQueryKey,
          (cache) => markFolderAsUnshared(cache, share.assetId),
        );
      }

      return { listSharesQueryKey, previousFolderTree, previousShares };
    },

    // Rollback on error
    onError: (_error, _newShare, context) => {
      if (!context) {
        return;
      }

      const { listSharesQueryKey, previousFolderTree, previousShares } =
        context;

      queryClient.setQueryData<ListSharesOutput | undefined>(
        listSharesQueryKey,
        () => previousShares,
      );

      if (previousFolderTree) {
        queryClient.setQueryData<FolderTreeOutput>(
          folderTreeQueryKey,
          () => previousFolderTree,
        );
      }
    },
  });
}
